import { getPathProductBySlug } from 'constants/Paths';
import { DOMAIN_WEB_HOST, ENV_COUNTRY, MAAC_CHANNEL_ID } from 'sysconfig';

/**
 * MAAC (Crescendo Lab) Web SDK — lớp DUY NHẤT biết về format event của MAAC.
 * Không component nào được tự push vào window.dataLayer.
 *
 * Cơ chế: plugin `clPluginDataLayer` monkey-patch window.dataLayer.push, validate mỗi entry
 * bằng zod rồi gọi clSdk.clRe.record(). Entry KHÔNG hợp lệ bị drop IM LẶNG (chỉ clDebug.warn,
 * không throw) — nên mọi field phải đúng type ngay từ đây.
 *
 * Format bắt buộc: { 0: 'event', 1: <event_name>, 2: <props> }
 * KHÔNG dùng { event, ecommerce } như snippet trong ticket BV-1372 — schema của plugin yêu cầu
 * object có key 0/1/2 (dạng `arguments` của gtag), nên format đó fail validate và bị drop.
 *
 * Chi tiết + schema đã verify: docs/maac-sdk-tracking-plan.md
 */

const MAAC_EVENT = {
  ADD_TO_CART: 'add_to_cart',
  REMOVE_FROM_CART: 'remove_from_cart',
  PURCHASE: 'purchase',
};

const mapCurrencyISO = {
  th: 'THB',
  vn: 'VND',
  kh: 'USD',
  cam: 'USD',
  en: 'USD',
};
const CURRENCY_ISO = mapCurrencyISO[ENV_COUNTRY] || 'THB';

// ─── Type coercion ───────────────────────────────────────────────────────────

/**
 * SDK CHỈ validate item_id + item_name; price/quantity/revenue đi qua .passthrough()
 * và KHÔNG được validate. Gửi string (vd '490') không hề báo lỗi nhưng hỏng data ở backend
 * — nên mọi field số bắt buộc đi qua hàm này.
 */
const toNumber = (value) => {
  const num = Number(value);
  return Number.isFinite(num) ? num : 0;
};

const toStr = (value) => (value === null || value === undefined ? '' : String(value));

/**
 * Chuyển số điện thoại TH sang E.164 (+66...). MAAC skip im lặng field phone thiếu country code.
 */
const convertPhoneNumber = (phone) => {
  try {
    if (!phone) return '';
    const value = toStr(phone).trim();
    if (!value) return '';
    if (value.startsWith('+66')) return value;
    if (value.startsWith('66')) return `+${value}`;
    if (value.startsWith('0')) return `+66${value.substring(1)}`;
    return `+66${value}`;
  } catch (error) {
    console.error('MAAC convertPhoneNumber error:', error);
  }
  return '';
};

/**
 * Gán DOMAIN_WEB_HOST làm prefix cho URL relative. URL đã absolute thì giữ nguyên.
 */
const toAbsoluteUrl = (path) => {
  if (!path) return '';
  if (path.startsWith('http://') || path.startsWith('https://') || path.startsWith('//')) {
    return path;
  }
  return `${DOMAIN_WEB_HOST}${path.startsWith('/') ? '' : '/'}${path}`;
};

// ─── dataLayer helpers ───────────────────────────────────────────────────────

const getDataLayer = () => {
  if (typeof window === 'undefined') return null;
  window.dataLayer = window.dataLayer || [];
  return window.dataLayer;
};

/**
 * Push ecommerce event theo native index format. items rỗng bị SDK drop nên chặn sớm ở đây.
 */
const pushEvent = (eventName, props) => {
  if (!MAAC_CHANNEL_ID) return;
  const dataLayer = getDataLayer();
  if (!dataLayer) return;
  try {
    if (!props?.items?.length) return;
    dataLayer.push({ 0: 'event', 1: eventName, 2: props });
  } catch (error) {
    console.error('MAAC pushEvent error:', error);
  }
};

// ─── Converters ──────────────────────────────────────────────────────────────

/**
 * Mapping product/cartItem → MAAC item.
 * item_id dùng `sku` — phải NHẤT QUÁN giữa add_to_cart và purchase, nếu không
 * Cart Retargeting không match được sản phẩm.
 *
 * KHÔNG reuse gtag.convertProduct: hàm đó gửi price dạng string (`${displayPrice}`),
 * đúng anti-pattern MAAC cảnh báo.
 */
const convertItem = (product) => {
  const item = {
    item_id: toStr(product?.sku),
    item_name: product?.name || '',
    // displayPrice ưu tiên: với sản phẩm combo, price gốc là 1000 nên phải lấy giá theo cartItem
    price: toNumber(product?.displayPrice ?? product?.price ?? product?.salePrice),
    quantity: toNumber(product?.quantity),
  };

  const itemBrand = product?.sellerInfo?.name || '';
  if (itemBrand) item.item_brand = itemBrand;

  if (product?.slug) {
    const link = toAbsoluteUrl(getPathProductBySlug(product.slug));
    if (link) item.link = link;
  }

  const imageLink = toAbsoluteUrl(product?.imagesProxy?.[0] || product?.imageUrls?.[0] || '');
  if (imageLink) item.image_link = imageLink;

  return item;
};

const convertItems = (products = []) =>
  (products || []).map(convertItem).filter((item) => item.item_id && item.item_name);

// ─── Public API ──────────────────────────────────────────────────────────────

/**
 * Cart event (add_to_cart / remove_from_cart).
 * Theo spec MAAC, event-level chỉ có `items` + `currency` — KHÔNG gửi `value`, vì SDK
 * transform `value` → `revenue`, sẽ làm bẩn số liệu revenue bằng event giỏ hàng.
 *
 * `product.quantity` mà caller truyền vào là DELTA (số lượng vừa thêm/bớt), không phải
 * tổng trong giỏ — đúng semantic "user adds an item to the cart" của MAAC.
 */
const trackCartEvent = (eventName, product) => {
  if (!product?.sku) return;
  const items = convertItems([product]);
  pushEvent(eventName, { currency: CURRENCY_ISO, items });
};

const trackAddToCart = (product) => trackCartEvent(MAAC_EVENT.ADD_TO_CART, product);

const trackRemoveFromCart = (product) => trackCartEvent(MAAC_EVENT.REMOVE_FROM_CART, product);

/**
 * Purchase event. Bắn tại thời điểm TẠO ĐƠN (không phải trang thankyou) để không mất event
 * ở 2 nhánh redirect waiting-payment / cổng thanh toán ngoài.
 *
 * transaction_id PHẢI là orderId (mã đơn thật) — SDK dedup theo field này, nên F5 hay bắn lại
 * cùng orderId sẽ không bị tính 2 lần.
 *
 * @param {object}   options
 * @param {string}   options.orderId  - Mã đơn, dùng làm transaction_id
 * @param {number}   options.revenue  - Tổng tiền đơn
 * @param {number}   [options.shipping]
 * @param {number}   [options.tax]
 * @param {string}   [options.coupon]
 * @param {object[]} [options.products] - Danh sách sản phẩm của đơn
 */
const trackPurchase = ({ orderId, revenue, shipping, tax, coupon, products = [] } = {}) => {
  if (!orderId) return;

  const props = {
    transaction_id: toStr(orderId),
    // SDK transform: value → revenue: Number(value). Gửi `value` là thoả field revenue bắt buộc.
    value: toNumber(revenue),
    currency: CURRENCY_ISO,
    items: convertItems(products),
  };

  if (shipping !== undefined && shipping !== null) props.shipping = toNumber(shipping);
  if (tax !== undefined && tax !== null) props.tax = toNumber(tax);
  if (coupon) props.coupon = toStr(coupon);

  pushEvent(MAAC_EVENT.PURCHASE, props);
};

// Chỉ push lại khi customerId đổi — tránh spam mỗi lần re-render/điều hướng.
let lastIdentifiedId = '';

/**
 * WebSDK Identify — gắn browsing footprint ẩn danh vào member để MAAC map sang LINE user.
 * Không có bước này thì workflow gửi LINE message sau khi đặt hàng KHÔNG chạy được.
 *
 * Lưu ý: clIdentity dùng format { event, params } — KHÁC hoàn toàn format {0,1,2} của
 * ecommerce event, vì clIdentity do core SDK xử lý, không đi qua clPluginDataLayer.
 *
 * Prerequisite phía MAAC: bật "Web footprint tracking" cho Web Channel. Nếu chưa bật,
 * MAAC bỏ qua toàn bộ identify và KHÔNG báo lỗi.
 */
const identify = (user) => {
  if (!MAAC_CHANNEL_ID) return;

  const customerId = toStr(user?.customerID || user?.code);
  // Không push cho khách ẩn danh: thiếu customerId thì MAAC không tạo/không update member.
  if (!customerId || customerId === lastIdentifiedId) return;

  const dataLayer = getDataLayer();
  if (!dataLayer) return;

  try {
    const params = { customerId };
    if (user?.email) params.email = user.email;
    if (user?.name) params.name = user.name;

    const phone = convertPhoneNumber(user?.phone || user?.phoneNumber);
    if (phone) params.phone = phone;

    dataLayer.push({ event: 'clIdentity', params });
    lastIdentifiedId = customerId;
  } catch (error) {
    // clIdentity là non-blocking, không được làm ảnh hưởng hoạt động của web.
    console.error('MAAC identify error:', error);
  }
};

export default {
  trackAddToCart,
  trackRemoveFromCart,
  trackPurchase,
  identify,
};
